// src/components/Portfolio/FinancialImpactDashboard.tsx
// Fixed to show actual financial exposure weighted by risk level

import React, { useMemo, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Paper, Tooltip, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';
import { PortfolioData } from '../../utils/portfolioHelpers';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface FinancialImpactDashboardProps {
  portfolio: PortfolioData;
}

type RiskFilter = 'high' | 'medium' | 'low';

// Risk-weighted exposure multipliers
// Higher risk = higher expected loss
const RISK_MULTIPLIERS: Record<string, number> = {
  high: 0.15,    // 15% expected loss for high risk (score > 6)
  medium: 0.05,  // 5% expected loss for medium risk (score > 3 and ≤ 6)
  low: 0.01,     // 1% expected loss for low risk (score > 0 and ≤ 3)
};

// Minimum risk score to count as "exposed" - any score above 0
const EXPOSURE_THRESHOLD = 0.1;

// Platform-wide risk thresholds
const RISK_THRESHOLDS = {
  high: 6,    // > 6 is high risk
  medium: 3,  // > 3 and ≤ 6 is medium risk
  // ≤ 3 is low risk
};

const FinancialImpactDashboard: React.FC<FinancialImpactDashboardProps> = ({ portfolio }) => {
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('high');

  const handleRiskFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: RiskFilter | null,
  ) => {
    if (newFilter !== null) {
      setRiskFilter(newFilter);
    }
  };

  // Calculate hazard statistics with proper financial exposure
  const hazardStats = useMemo(() => {
    if (!portfolio?.items?.length) {
      return [];
    }

    const hazardMap = new Map<string, { 
      count: number; 
      totalValue: number;           // Total value of exposed properties
      riskWeightedExposure: number; // Risk-weighted financial exposure
      highRiskCount: number;
      highRiskValue: number;
      mediumRiskCount: number;
      mediumRiskValue: number;
      lowRiskCount: number;
      lowRiskValue: number;
      avgRiskScore: number;
      totalRiskScore: number;
    }>();

    // Define all 18 climate hazards
    const hazards = [
      'flood', 'wildfire', 'earthquake', 'heat', 'drought', 'hurricane',
      'tornado', 'hail', 'lightning', 'avalanche', 'cold', 'ice',
      'landslide', 'tsunami', 'volcanic', 'wind', 'winter'
    ];

    // Determine minimum score based on risk filter
    const getMinScoreForFilter = (): number => {
      switch (riskFilter) {
        case 'high': return RISK_THRESHOLDS.high; // > 6
        case 'medium': return RISK_THRESHOLDS.medium; // > 3
        case 'low': return EXPOSURE_THRESHOLD; // > 0
        default: return RISK_THRESHOLDS.high;
      }
    };

    const getMaxScoreForFilter = (): number => {
      switch (riskFilter) {
        case 'high': return 10;
        case 'medium': return RISK_THRESHOLDS.high; // ≤ 6
        case 'low': return RISK_THRESHOLDS.medium; // ≤ 3
        default: return 10;
      }
    };

    const minScore = getMinScoreForFilter();
    const maxScore = getMaxScoreForFilter();

    portfolio.items.forEach((property: any) => {
      const assetValue = typeof property.asset_value === 'string' 
        ? parseFloat(property.asset_value.replace(/[$,]/g, '')) 
        : property.asset_value || 0;

      hazards.forEach(hazard => {
        const riskScore = parseFloat(property[hazard]) || 0;
        
        // Filter based on selected risk level
        const meetsFilter = riskFilter === 'low' 
          ? (riskScore > minScore && riskScore <= maxScore)
          : (riskScore > minScore && riskScore <= maxScore);

        if (meetsFilter) {
          const current = hazardMap.get(hazard) || { 
            count: 0, 
            totalValue: 0,
            riskWeightedExposure: 0,
            highRiskCount: 0,
            highRiskValue: 0,
            mediumRiskCount: 0,
            mediumRiskValue: 0,
            lowRiskCount: 0,
            lowRiskValue: 0,
            avgRiskScore: 0,
            totalRiskScore: 0,
          };
          
          current.count += 1;
          current.totalValue += assetValue;
          current.totalRiskScore += riskScore;
          
          // Calculate risk-weighted exposure based on severity
          // Platform-wide thresholds: High > 6, Medium > 3 and ≤ 6, Low ≤ 3
          let riskMultiplier: number;
          if (riskScore > RISK_THRESHOLDS.high) {
            current.highRiskCount += 1;
            current.highRiskValue += assetValue;
            riskMultiplier = RISK_MULTIPLIERS.high;
          } else if (riskScore > RISK_THRESHOLDS.medium) {
            current.mediumRiskCount += 1;
            current.mediumRiskValue += assetValue;
            riskMultiplier = RISK_MULTIPLIERS.medium;
          } else {
            current.lowRiskCount += 1;
            current.lowRiskValue += assetValue;
            riskMultiplier = RISK_MULTIPLIERS.low;
          }
          
          // Risk-weighted exposure = asset value × risk multiplier × (risk score / 10)
          // This gives higher exposure for higher risk scores
          current.riskWeightedExposure += assetValue * riskMultiplier * (riskScore / 10);
          
          hazardMap.set(hazard, current);
        }
      });
    });

    return Array.from(hazardMap.entries())
      .map(([hazard, stats]) => ({
        hazard,
        properties: stats.count,
        totalValue: stats.totalValue,
        exposure: stats.riskWeightedExposure,
        highRisk: stats.highRiskCount,
        highRiskValue: stats.highRiskValue,
        mediumRisk: stats.mediumRiskCount,
        mediumRiskValue: stats.mediumRiskValue,
        lowRisk: stats.lowRiskCount,
        lowRiskValue: stats.lowRiskValue,
        avgRiskScore: stats.count > 0 ? stats.totalRiskScore / stats.count : 0,
      }))
      .sort((a, b) => b.exposure - a.exposure); // Sort by financial exposure
  }, [portfolio, riskFilter]);

  // Calculate total portfolio value
  const totalPortfolioValue = useMemo(() => {
    if (!portfolio?.items?.length) return 0;
    return portfolio.items.reduce((sum: number, prop: any) => {
      const assetValue = typeof prop.asset_value === 'string' 
        ? parseFloat(prop.asset_value.replace(/[$,]/g, '')) 
        : prop.asset_value || 0;
      return sum + assetValue;
    }, 0);
  }, [portfolio]);

  // Calculate total risk-weighted exposure
  const totalExposure = useMemo(() => {
    return hazardStats.reduce((sum, h) => sum + h.exposure, 0);
  }, [hazardStats]);

  // Prepare radar chart data - by property count
  const radarData = useMemo(() => {
    const top10Hazards = [...hazardStats]
      .sort((a, b) => b.properties - a.properties)
      .slice(0, 10);
    return top10Hazards.map(h => ({
      hazard: h.hazard.replace(/_/g, ' '),
      properties: h.properties,
    }));
  }, [hazardStats]);

  // Get colors for bars based on exposure level
  const getBarColor = (exposure: number, maxExposure: number) => {
    const ratio = exposure / maxExposure;
    if (ratio > 0.7) return '#ef4444'; // Red - highest exposure
    if (ratio > 0.5) return '#f97316'; // Orange
    if (ratio > 0.3) return '#f59e0b'; // Amber
    if (ratio > 0.15) return '#eab308'; // Yellow
    return '#22c55e'; // Green - lowest exposure
  };

  const formatCurrency = (value: number): string => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  if (!portfolio?.items?.length) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No portfolio data available
        </Typography>
      </Paper>
    );
  }

  const maxExposure = hazardStats.length > 0 ? hazardStats[0].exposure : 1;

  // Get filter description
  const getFilterDescription = () => {
    switch (riskFilter) {
      case 'high': return 'Showing properties with risk score > 6';
      case 'medium': return 'Showing properties with risk score 3.1 - 6';
      case 'low': return 'Showing properties with risk score ≤ 3';
      default: return '';
    }
  };

  return (
    <Box>
      {/* Header with Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Financial Impact Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getFilterDescription()}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Filter by Risk Level
          </Typography>
          <ToggleButtonGroup
            value={riskFilter}
            exclusive
            onChange={handleRiskFilterChange}
            aria-label="risk filter"
            size="small"
          >
            <ToggleButton 
              value="high" 
              aria-label="high risk"
              sx={{ 
                '&.Mui-selected': { 
                  bgcolor: '#FEE2E2', 
                  color: '#991B1B',
                  '&:hover': { bgcolor: '#FECACA' }
                }
              }}
            >
              <ErrorIcon sx={{ mr: 0.5, fontSize: 18 }} />
              High Risk
            </ToggleButton>
            <ToggleButton 
              value="medium" 
              aria-label="medium risk"
              sx={{ 
                '&.Mui-selected': { 
                  bgcolor: '#FEF3C7', 
                  color: '#92400E',
                  '&:hover': { bgcolor: '#FDE68A' }
                }
              }}
            >
              <WarningIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Medium Risk
            </ToggleButton>
            <ToggleButton 
              value="low" 
              aria-label="low risk"
              sx={{ 
                '&.Mui-selected': { 
                  bgcolor: '#D1FAE5', 
                  color: '#065F46',
                  '&:hover': { bgcolor: '#A7F3D0' }
                }
              }}
            >
              <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Low Risk
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Properties
              </Typography>
              <Typography variant="h4">
                {portfolio.items.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analyzed locations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Portfolio Value
              </Typography>
              <Typography variant="h4">
                {formatCurrency(totalPortfolioValue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Combined asset value
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#FEF3C7' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                  Risk-Weighted Exposure
                </Typography>
                <Tooltip title="Expected financial loss based on hazard risk levels. Calculated as: Asset Value × Risk Factor × (Risk Score / 10). Higher risk properties contribute more to total exposure.">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="h4" color="warning.dark">
                {formatCurrency(totalExposure)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {((totalExposure / totalPortfolioValue) * 100).toFixed(2)}% of portfolio value
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Hazards Identified
              </Typography>
              <Typography variant="h4">
                {hazardStats.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Climate hazards with exposure
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Radar Chart - Top Hazards by Property Count */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Hazards by Property Count
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Number of properties with any hazard exposure
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="hazard" />
                <PolarRadiusAxis />
                <Radar
                  name="Properties"
                  dataKey="properties"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                />
                <Legend />
                <RechartsTooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Bar Chart - Financial Exposure by Hazard */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="h6">
                Risk-Weighted Financial Exposure
              </Typography>
              <Tooltip title="Expected loss = Asset Value × Risk Factor (15% high, 5% medium, 1% low) × (Risk Score / 10)">
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Estimated financial impact by hazard type
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={hazardStats.slice(0, 12)} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hazard" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                  interval={0}
                />
                <YAxis 
                  width={70}
                  tickFormatter={(value) => {
                    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
                    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
                    return `$${value}`;
                  }}
                  tick={{ fontSize: 11 }}
                />
                <RechartsTooltip 
                  formatter={(value: number, name: string) => {
                    if (name === 'exposure') return [formatCurrency(value), 'Risk-Weighted Exposure'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => label.replace(/_/g, ' ').toUpperCase()}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                            {label.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="body2" color="warning.dark" fontWeight={600}>
                            Exposure: {formatCurrency(data.exposure)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Properties: {data.properties}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Avg Risk Score: {data.avgRiskScore.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="error.main">
                            High Risk: {data.highRisk} ({formatCurrency(data.highRiskValue)})
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="exposure">
                  {hazardStats.slice(0, 12).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.exposure, maxExposure)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Detailed Hazard Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              All Climate Hazards - Detailed Breakdown
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Risk-weighted exposure calculated using platform-wide thresholds: High (&gt;6), Medium (3.1-6), Low (≤3)
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Hazard Type</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Properties</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Avg Risk</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>High Risk</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Medium Risk</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Low Risk</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Value at Risk</th>
                    <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#FEF3C7' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        Exposure
                        <Tooltip title="Risk-weighted expected loss">
                          <InfoIcon sx={{ fontSize: 14 }} />
                        </Tooltip>
                      </Box>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hazardStats.map((hazard, index) => (
                    <tr 
                      key={hazard.hazard}
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        {hazard.hazard.replace(/_/g, ' ').toUpperCase()}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {hazard.properties}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <Chip 
                          label={hazard.avgRiskScore.toFixed(1)}
                          size="small"
                          sx={{ 
                            bgcolor: hazard.avgRiskScore > 6 ? '#FEE2E2' : 
                                    hazard.avgRiskScore > 3 ? '#FEF3C7' : '#D1FAE5',
                            color: hazard.avgRiskScore > 6 ? '#991B1B' :
                                   hazard.avgRiskScore > 3 ? '#92400E' : '#065F46',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444' }}>
                        {hazard.highRisk}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#f59e0b' }}>
                        {hazard.mediumRisk}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#22c55e' }}>
                        {hazard.lowRisk}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {formatCurrency(hazard.totalValue)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', backgroundColor: '#FEF3C7', fontWeight: 600 }}>
                        {formatCurrency(hazard.exposure)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e0e0e0', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <td style={{ padding: '12px' }}>TOTAL</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444' }}>
                      {hazardStats.reduce((sum, h) => sum + h.highRisk, 0)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#f59e0b' }}>
                      {hazardStats.reduce((sum, h) => sum + h.mediumRisk, 0)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#22c55e' }}>
                      {hazardStats.reduce((sum, h) => sum + h.lowRisk, 0)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {formatCurrency(totalPortfolioValue)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', backgroundColor: '#FCD34D' }}>
                      {formatCurrency(totalExposure)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Box>

            {/* Methodology Note */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Methodology
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Risk-Weighted Exposure</strong> = Asset Value × Risk Factor × (Risk Score / 10)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Risk Factors: <span style={{ color: '#EF4444' }}>High Risk (&gt;6)</span> = 15%, <span style={{ color: '#F59E0B' }}>Medium Risk (3.1-6)</span> = 5%, <span style={{ color: '#10B981' }}>Low Risk (≤3)</span> = 1%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Values represent estimated annual expected loss based on historical loss ratios from FEMA NRI data.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinancialImpactDashboard;